import { type CSSProperties, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, GripVertical, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { CharacterCount } from '@/components/CharacterCount';
import { SECTION_TITLE_MAX } from '@/features/product/field-limits';
import { archivedProductMessage } from '@/features/product/archived';
import { ApiConflictError } from '@/lib/api-client';
import type { SectionChangeKind } from '../api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSaveTechSpecMarkdown } from '../hooks';
import { headingText, normalizeForSearch, searchTerms } from './section-search';
import { SpecMarkdown } from './SpecMarkdown';
import { VisualSectionEditor } from './VisualSectionEditor';
import { combineSection, resolveFinalize, sectionBody, unescape } from './sectionTitle';

/**
 * Editable outline of a section-approved product's spec, on the edit page. The spec is the
 * kept sections joined in order, each starting with its `#`/`##` heading; we split it back
 * at those headings into a collapsible row per section so it's navigable, and let the owner
 * edit any section's markdown or add a new one. Every change writes `Product.tech_spec_markdown`
 * (the agent's source of truth) via autosave — the original document stays untouched.
 *
 * Each section edits as a required Title field + a body editor (not one blob) so its `## ` heading
 * can never be deleted — a headingless section silently merges into its neighbour on the next
 * reload. Storage is unchanged: the title + body reassemble to the same `## {title}\n\n{body}`.
 */

/** Heading of a section's markdown (its first `#`/`##` line); front matter → "Document header". */
function headingOf(md: string): string {
  const nl = md.indexOf('\n');
  const first = (nl === -1 ? md : md.slice(0, nl)).trim();
  const m = first.match(/^#{1,2}\s+(.+?)\s*#*\s*$/);
  return m ? unescape((m[1] ?? '').trim()) : 'Document header';
}

/** Split the joined spec back into one full-markdown string per section (heading included). */
function splitToRaw(markdown: string): string[] {
  return markdown
    .split(/\n(?=#{1,2} )/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Whether a section starts with its own `#`/`##` heading line. The front-matter "Document
 *  header" has none — and a headingless section has no marker to split back out, so on reload it
 *  would merge into whatever precedes it. See canMove. */
function hasHeading(md: string): boolean {
  const nl = md.indexOf('\n');
  const first = (nl === -1 ? md : md.slice(0, nl)).trim();
  return /^#{1,2}\s+/.test(first);
}

interface Section {
  id: number;
  md: string;
  /** Bumped when the text is replaced from OUTSIDE the open editor (toast Undo). The edit region
   *  and body editor seed themselves from `md` once and then own it, so an outside change has to
   *  remount them — the row keys the editor on this. */
  rev?: number;
}

/** New section order after dragging `activeId` onto `overId`'s slot. The headingless front-matter
 *  section ("Document header") is kept pinned first — it has no `##` marker, so anything landing
 *  above it would merge into it on reload. Anything dropped "above" the header lands just below it
 *  instead. Returns the same array reference when nothing changes. Exported for the guard test. */
export function reorderWithPin(arr: Section[], activeId: number, overId: number): Section[] {
  const from = arr.findIndex((s) => s.id === activeId);
  const to = arr.findIndex((s) => s.id === overId);
  if (from < 0 || to < 0 || from === to) return arr;
  const moved = arrayMove(arr, from, to);
  const first = arr[0];
  if (first && !hasHeading(first.md) && moved[0]?.id !== first.id) {
    return [first, ...moved.filter((s) => s.id !== first.id)];
  }
  return moved;
}

/** The edit UI for one section that has (or must get) a heading: a required Title input + the
 *  existing body editor, reassembled to `## {title}\n\n{body}` on commit. Mounted only while
 *  editing, so each edit session re-seeds its title/body from the CURRENT `section.md` (no stale
 *  draft on re-open) and its unmount cleanup is the natural finalize hook.
 *
 *  Commit coordination: the body editor is uncontrolled (a keystroke must not re-render the long
 *  outline), so the latest title + body are combined via refs — either field changing produces the
 *  combined commit. Enforcement ("block until titled"): Done with an empty title is intercepted and
 *  keeps the row in edit mode; a focus-leave / unmount with content but no title auto-names the
 *  section "Untitled section"; an abandoned add (empty + empty) is discarded. */
function SectionEditRegion({
  section,
  isFreshAdd,
  onCommit,
  onDiscard,
  onLeaveEdit,
}: {
  section: Section;
  /** True for a just-added, never-committed card (empty `md`) — the only case abandon can discard. */
  isFreshAdd: boolean;
  onCommit: (id: number, md: string) => void;
  onDiscard: (id: number) => void;
  onLeaveEdit: () => void;
}) {
  // Session seeds, captured once at mount (a new edit session = a fresh mount = fresh seeds). Lazy
  // useState initializers run exactly once, so they stay pinned to the mount-time `section.md` even
  // if the prop advances mid-session (e.g. an autosave commits and the parent re-renders).
  const [originalMd] = useState(() => section.md);
  const [seedTitle] = useState(() => (hasHeading(section.md) ? headingOf(section.md) : ''));
  const [seedBody] = useState(() => sectionBody(section.md));

  const [title, setTitle] = useState(seedTitle);
  // Whether Done was pressed on an empty title — surfaces the "Add a title" hint + field highlight.
  const [blocked, setBlocked] = useState(false);

  // Refs mirror the latest title/body so the (memo-friendly) combine reads them without going
  // stale — the body editor only reports its markdown via onCommit (on change), so bodyRef holds
  // the last reported body, seeded to the unedited body.
  const titleRef = useRef(seedTitle);
  const bodyRef = useRef(seedBody);
  const lastPushed = useRef(originalMd);
  // Whether the user actually edited this session (title or body). Gates the UNMOUNT finalize so
  // React StrictMode's dev mount→cleanup→mount can't fire it on a pristine fresh add — which would
  // resolve to "discard" and vanish the just-added card. Real abandon still discards via blur (a
  // user event StrictMode never simulates); an untouched unmount has nothing to save anyway.
  const touchedRef = useRef(false);

  const push = (effectiveTitle: string) => {
    const md = combineSection({
      originalMd,
      initialTitle: seedTitle,
      initialBody: seedBody,
      title: effectiveTitle,
      body: bodyRef.current,
    });
    if (md === lastPushed.current) return; // no byte change -> true no-op (dedupes repeat commits)
    lastPushed.current = md;
    onCommit(section.id, md);
  };

  // Resolve an ended session: commit / auto-name / discard. Used on focus-leave and on unmount.
  const finalize = () => {
    const res = resolveFinalize({
      title: titleRef.current,
      bodyEmpty: bodyRef.current.trim() === '',
      // Derive "abandoned fresh add" from refs, NOT the closed-over `isFreshAdd` prop, so the
      // unmount closure (mount-time prop) and the blur path (current prop) agree. Discard only a
      // fresh add that was NEVER committed this session; once it has been committed+persisted
      // (lastPushed advanced past the empty originalMd), an emptied session must AUTO-NAME —
      // else discard would drop it locally while the DB keeps `## Foo`, so it reappears on reload.
      isFreshAdd: originalMd.trim() === '' && lastPushed.current === originalMd,
    });
    if (res.kind === 'discard') {
      onDiscard(section.id);
      return;
    }
    if (res.title !== titleRef.current.trim()) {
      titleRef.current = res.title; // auto-name safety net
      setTitle(res.title);
    }
    push(res.title);
  };

  // Commit on unmount (SPA nav, search-filter, Preview toggle, delete) so an in-progress edit is
  // never lost — but only if the session was actually touched (see touchedRef). `finalize` reads
  // only refs + stable seeds/callbacks, so the mount-time closure is correct at call time; run it
  // once as the cleanup. Idempotent via lastPushed.
  useEffect(
    () => () => {
      if (touchedRef.current) finalize();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // The body editor reports its markdown on change/blur/Done. Combine it with the title — but only
  // actually save while a title exists, so a body typed before a title can never persist a
  // title-less `## \n\n…` heading (that would merge on reload). Untitled body is just stashed until
  // the title is added (Done) or the section is left (auto-named).
  const onBodyCommit = (bodyMd: string) => {
    touchedRef.current = true;
    bodyRef.current = bodyMd;
    if (titleRef.current.trim()) push(titleRef.current.trim());
  };

  // Done: block (stay in edit mode) while the title is empty; otherwise commit + leave edit mode.
  const onDone = () => {
    if (!titleRef.current.trim()) {
      setBlocked(true);
      return;
    }
    push(titleRef.current.trim());
    onLeaveEdit();
  };

  const titleEmpty = title.trim() === '';
  return (
    <div
      // A genuine focus-leave of the whole region (not a click between the two fields) finalizes —
      // autosave on click-away, matching the body editor's own blur-commit.
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) finalize();
      }}
    >
      <label className="mb-2 block">
        <span className="mb-1 block text-[11.5px] font-semibold uppercase tracking-wide text-muted">
          Section title
        </span>
        <input
          type="text"
          aria-label="Section title"
          value={title}
          maxLength={SECTION_TITLE_MAX}
          autoFocus={isFreshAdd}
          onChange={(e) => {
            const v = e.target.value;
            touchedRef.current = true;
            setTitle(v);
            titleRef.current = v;
            if (v.trim()) setBlocked(false);
          }}
          placeholder="e.g. Device Description"
          className={cn(
            'w-full rounded-lg border bg-white px-3 py-2 text-[13.5px] font-semibold text-navy placeholder:font-normal placeholder:text-muted focus:outline-none',
            titleEmpty
              ? 'border-state-error-accent focus:border-state-error-accent'
              : 'border-line focus:border-electric',
          )}
        />
        {titleEmpty && (
          <span
            className={cn(
              'mt-1 block text-[12px] font-medium',
              blocked ? 'text-state-error-text' : 'text-muted',
            )}
          >
            Add a title — every section needs one so it can’t merge into the section above it.
          </span>
        )}
        {/* Shown whenever there is a title, same as the product name — one open editor at a
            time, so this is never a hundred counters at once. */}
        {title ? (
          <CharacterCount value={title} max={SECTION_TITLE_MAX} className="mt-1" />
        ) : null}
      </label>
      <VisualSectionEditor
        value={seedBody}
        autoFocus={!isFreshAdd}
        onCommit={onBodyCommit}
        onDone={onDone}
      />
    </div>
  );
}

/** One collapsible section row — view its rendered markdown, or edit it in place. Keeps the
 *  draft local while editing so a keystroke doesn't re-render the whole (long) outline;
 *  commits to the parent on blur / Done. Memoized against the stable parent callbacks. */
const SpecSectionRow = memo(function SpecSectionRow({
  section,
  open,
  autoEdit,
  dragHandle,
  onToggleOpen,
  onCommit,
  onDiscard,
  onRequestDelete,
}: {
  section: Section;
  open: boolean;
  autoEdit: boolean;
  /** Drag-handle props (attributes + listeners) from the sortable wrapper, or null when reorder
   *  isn't available here — the front-matter section (not draggable) or a filtered/search view. */
  dragHandle: Pick<ReturnType<typeof useSortable>, 'attributes' | 'listeners'> | null;
  onToggleOpen: (id: number) => void;
  onCommit: (id: number, md: string) => void;
  /** Silently drop an abandoned, never-committed add (empty title + empty body). No save. */
  onDiscard: (id: number) => void;
  onRequestDelete: (id: number) => void;
}) {
  const [editing, setEditing] = useState(autoEdit);
  // A fresh add starts with empty markdown; front matter is the headingless (non-empty) preamble.
  const isFreshAdd = section.md.trim() === '';
  const isFrontMatter = !isFreshAdd && !hasHeading(section.md);
  const heading = isFreshAdd ? 'New section' : headingOf(section.md);
  const body = sectionBody(section.md);

  // A just-added section is appended to the end of a long outline — bring it into view so the
  // "Add section" button (up in the header) gives immediate feedback instead of silently landing
  // 100 rows down (the visual editor focuses itself via autoFocus). Runs once, on the new row.
  const rootRef = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (autoEdit) rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <details
      ref={rootRef}
      open={open || editing}
      className="group open:bg-line-soft/20"
    >
      <summary
        onClick={(e) => {
          e.preventDefault();
          onToggleOpen(section.id);
        }}
        className="flex cursor-pointer list-none items-center gap-2 px-4 py-2.5 text-[13.5px] font-semibold text-navy hover:bg-line-soft/40"
      >
        {/* Drag handle. onClick stopPropagation so a plain click on the grip doesn't toggle the
            row; a drag (>5px, per the PointerSensor) starts a reorder. Absent for the pinned
            front-matter section and while searching (dragHandle is null). */}
        {dragHandle ? (
          <button
            {...dragHandle.attributes}
            {...dragHandle.listeners}
            type="button"
            aria-label="Drag to reorder section"
            title="Drag to reorder"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="inline-flex size-6 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted hover:bg-line-soft hover:text-navy active:cursor-grabbing"
          >
            <GripVertical className="size-4" aria-hidden />
          </button>
        ) : (
          <span className="size-6 shrink-0" aria-hidden />
        )}
        <ChevronDown
          className="size-4 shrink-0 -rotate-90 text-muted transition-transform group-open:rotate-0"
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate" title={heading}>
          {heading}
        </span>
      </summary>
      {/* Only render the (expensive react-markdown) body when the row is actually open. A closed
          <details> keeps its children in the DOM, so rendering all 100+ bodies up front — and
          re-rendering them on every drag frame — is what made the outline heavy and dragging laggy. */}
      {(open || editing) && (
        <div className="border-t border-line-soft bg-white px-4 pt-3 pb-2">
          <div className="mb-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => onRequestDelete(section.id)}
              className="inline-flex shrink-0 items-center gap-1 rounded border border-line px-2 py-1 text-[12px] font-semibold text-muted hover:border-state-error-accent hover:text-state-error-text"
            >
              <Trash2 className="size-3.5" aria-hidden /> Delete
            </button>
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex shrink-0 items-center gap-1 rounded border border-line px-2 py-1 text-[12px] font-semibold text-navy hover:border-electric hover:text-electric"
              >
                <Pencil className="size-3.5" aria-hidden /> Edit
              </button>
            )}
          </div>
          {editing ? (
            isFrontMatter ? (
              // The headingless front-matter preamble is the one legitimate title-less section —
              // edit it body-only, exactly as before (no title to force, stays pinned).
              <VisualSectionEditor
                key={section.rev ?? 0}
                value={section.md}
                autoFocus
                onCommit={(md) => onCommit(section.id, md)}
                onDone={() => setEditing(false)}
              />
            ) : (
              <SectionEditRegion
                key={section.rev ?? 0}
                section={section}
                isFreshAdd={isFreshAdd}
                onCommit={onCommit}
                onDiscard={onDiscard}
                onLeaveEdit={() => setEditing(false)}
              />
            )
          ) : (
            <SpecMarkdown markdown={body || '_Empty — click Edit to add content._'} />
          )}
        </div>
      )}
    </details>
  );
});

/** Sortable wrapper: makes one row draggable and carries the row-separator border. The
 *  front-matter section (no heading) is drag-disabled so it stays pinned at the top. Memoized so
 *  a parent re-render (e.g. the autosave status flipping) doesn't re-render all 100+ rows — only
 *  the rows whose own props change, plus whatever dnd-kit re-renders during an actual drag. */
const SortableSpecRow = memo(function SortableSpecRow(
  props: {
    section: Section;
    open: boolean;
    autoEdit: boolean;
    onToggleOpen: (id: number) => void;
    onCommit: (id: number, md: string) => void;
    onDiscard: (id: number) => void;
    onRequestDelete: (id: number) => void;
  },
) {
  const draggable = hasHeading(props.section.md);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.section.id,
    disabled: !draggable,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.4 : undefined,
  };
  // Memoize the handle object so memo(SpecSectionRow) can actually skip: a fresh {attributes,
  // listeners} each render would otherwise re-render (and re-parse an open section) every frame.
  const dragHandle = useMemo(
    () => (draggable ? { attributes, listeners } : null),
    [draggable, attributes, listeners],
  );
  return (
    <div ref={setNodeRef} style={style} className="border-b border-line-soft bg-white last:border-b-0">
      <SpecSectionRow {...props} dragHandle={dragHandle} />
    </div>
  );
});

export function StructuredSpecView({
  productId,
  markdown,
  updatedAt,
}: {
  productId: number;
  markdown: string;
  /** The product's `updated_at` from the edit snapshot — the optimistic-concurrency token. */
  updatedAt: string;
}) {
  const idRef = useRef(0);
  const [sections, setSections] = useState<Section[]>(() =>
    splitToRaw(markdown).map((md) => ({ id: idRef.current++, md })),
  );
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [justAdded, setJustAdded] = useState<number | null>(null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  // A pending delete awaiting confirmation (delete is destructive + autosaves, so it's gated).
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [query, setQuery] = useState('');

  // sectionsRef mirrors state so the stable (memo-friendly) callbacks below always read the
  // latest sections without going stale, and without a side effect inside a setState updater
  // (which React double-invokes in dev StrictMode — that would fire the save twice).
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  const save = useSaveTechSpecMarkdown(productId);
  const mutate = save.mutate;
  // Optimistic-concurrency token = the product's updated_at. Seeded from the snapshot, advanced
  // on each successful save, and re-synced from the prop when the snapshot refetches (a same-page
  // product-name save bumps updated_at) so our own cross-column edits don't false-conflict.
  const ifMatchRef = useRef(updatedAt);
  useEffect(() => {
    if (updatedAt) ifMatchRef.current = updatedAt;
  }, [updatedAt]);
  // Set once another writer has won a 409 — we stop saving (no blind clobber) until reload.
  const [conflicted, setConflicted] = useState(false);

  const persist = useCallback(
    (list: Section[], changeKind: SectionChangeKind) => {
      if (conflicted) return;
      const md = list.map((s) => s.md.trim()).filter(Boolean).join('\n\n');
      mutate(
        { markdown: md, changeKind, ifMatch: ifMatchRef.current },
        {
          onSuccess: (res) => {
            ifMatchRef.current = res.updated_at;
          },
          onError: (err) => {
            if (err instanceof ApiConflictError) {
              // Two different 409s share this endpoint; an archived product says so in
              // the server's own words.
              setConflicted(true);
              toast.error(
                archivedProductMessage(err) ??
                  'This spec was changed elsewhere. Reload the page to see the latest.',
              );
            } else {
              toast.error('Could not save your changes. Please try again.');
            }
          },
        },
      );
    },
    [mutate, conflicted],
  );

  const apply = useCallback(
    (next: Section[], changeKind: SectionChangeKind) => {
      sectionsRef.current = next;
      setSections(next);
      persist(next, changeKind);
    },
    [persist],
  );

  const onCommit = useCallback(
    (id: number, md: string) => {
      const prev = sectionsRef.current.find((s) => s.id === id);
      if (!prev || prev.md === md) return;
      const prevMd = prev.md;
      apply(sectionsRef.current.map((s) => (s.id === id ? { ...s, md } : s)), 'edit');
      // An edit autosaves, so an accidental change is otherwise irreversible — offer a one-tap
      // Undo that restores just this section's previous text (leaving any other edits intact).
      const label = headingOf(md) || 'Section';
      toast.success(`Edited “${label.length > 44 ? `${label.slice(0, 44)}…` : label}”`, {
        action: {
          label: 'Undo',
          onClick: () =>
            apply(
              sectionsRef.current.map((s) =>
                s.id === id ? { ...s, md: prevMd, rev: (s.rev ?? 0) + 1 } : s,
              ),
              'edit',
            ),
        },
        duration: 6000,
      });
    },
    [apply],
  );
  // Actually remove a section — only reached after the confirm dialog is accepted.
  const deleteSection = useCallback(
    (id: number) => {
      apply(sectionsRef.current.filter((s) => s.id !== id), 'delete');
      setOpen((o) => {
        const { [id]: _drop, ...rest } = o;
        return rest;
      });
    },
    [apply],
  );
  // Silently drop an abandoned add — a just-added card left with an empty title AND empty body.
  // It was never persisted (addSection doesn't save an empty section), so there's nothing to save:
  // just remove it locally. No confirm dialog, no "at least one section" gate (it added nothing).
  const discardSection = useCallback((id: number) => {
    const next = sectionsRef.current.filter((s) => s.id !== id);
    sectionsRef.current = next;
    setSections(next);
    setOpen((o) => {
      const { [id]: _drop, ...rest } = o;
      return rest;
    });
    setJustAdded((j) => (j === id ? null : j));
  }, []);
  const onRequestDelete = useCallback((id: number) => {
    // Keep at least one section: an empty spec would store NULL and, on the next load, flip the
    // edit page back to the key:value editor mid-flow. Delete the content by editing, not this.
    if (sectionsRef.current.length <= 1) {
      toast.error('A spec needs at least one section — edit this one instead of deleting it.');
      return;
    }
    setPendingDelete(id);
  }, []);
  const onToggleOpen = useCallback((id: number) => {
    setOpen((o) => ({ ...o, [id]: !o[id] }));
  }, []);
  // Drag-to-reorder. PointerSensor waits 5px so a click on the grip doesn't start a drag;
  // KeyboardSensor makes reordering accessible (grip focused → space, arrows, space).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const prev = sectionsRef.current;
      const next = reorderWithPin(prev, active.id as number, over.id as number);
      if (next === prev) return;
      const prevOrder = prev.map((s) => s.id);
      apply(next, 'reorder');
      // Instant, with a one-tap Undo so an accidental drop is easy to take back. toast.success —
      // the app's Toaster only styles the richColors variants.
      const moved = prev.find((s) => s.id === active.id);
      const label = moved ? headingOf(moved.md) : 'Section';
      toast.success(`Moved “${label.length > 44 ? `${label.slice(0, 44)}…` : label}”`, {
        action: {
          label: 'Undo',
          // Order-only undo: re-sort the LIVE sections back to the pre-drag order, preserving any
          // edits / deletes / adds made during the toast window. Never restore the captured
          // snapshot wholesale — that would resurrect a deleted section and revert other edits.
          onClick: () => {
            const byId = new Map(sectionsRef.current.map((s) => [s.id, s]));
            const prevIds = new Set(prevOrder);
            const restored = prevOrder
              .map((id) => byId.get(id))
              .filter((s): s is Section => Boolean(s));
            const added = sectionsRef.current.filter((s) => !prevIds.has(s.id));
            apply([...restored, ...added], 'reorder');
          },
        },
        duration: 6000,
      });
    },
    [apply],
  );

  const addSection = () => {
    const id = idRef.current++;
    // Add an EMPTY card (no `## New section` placeholder to delete) and DON'T persist it — an empty
    // section is dropped by the join anyway, so saving now would be a spurious no-op write. It
    // persists on the first real commit (via onCommit), or is discarded if abandoned untouched.
    const next = [...sectionsRef.current, { id, md: '' }];
    sectionsRef.current = next;
    setSections(next);
    setOpen((o) => ({ ...o, [id]: true }));
    setJustAdded(id);
    setQuery(''); // a search filter would hide the just-added card — clear it so it shows
  };

  // Section search filters by TITLE only (every word of the query, any order, ignoring
  // case, Markdown marks and punctuation — see section-search.ts). Body text is not
  // searched: a common word can appear in half the document, and the user is looking
  // for the section they can name.
  const q = query.trim();
  const filtering = searchTerms(q).length > 0;
  // Precompute normalised titles once per `sections` change (not per keystroke / render).
  const titles = useMemo(
    () => sections.map((s) => ({ s, head: normalizeForSearch(headingText(s.md)) })),
    [sections],
  );
  const visible = useMemo(() => {
    const terms = searchTerms(q);
    if (terms.length === 0) return sections;
    return titles.filter((h) => h.head && terms.every((t) => h.head.includes(t))).map((h) => h.s);
  }, [titles, q, sections]);
  // Stable id list for SortableContext so it isn't rebuilt on unrelated re-renders (save status).
  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);
  // Whole-document preview markdown, memoized so a name-card save / status flip doesn't re-join
  // and (via memo(SpecMarkdown)) re-parse the 86 KB document on an unrelated re-render.
  const previewMd = useMemo(
    () => sections.map((s) => s.md.trim()).filter(Boolean).join('\n\n'),
    [sections],
  );

  return (
    <section className="rounded-2xl border border-line bg-white p-6 shadow-card">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="m-0 text-[18px] font-bold text-navy">Technical Specification</h2>
        <div className="flex items-center gap-3">
          {/* Toggle the whole card between the editable outline and a read-only preview of the
              WHOLE document (all sections joined, in order) — so the owner can see how it reads
              end to end. */}
          <div className="inline-flex rounded-lg border border-line p-0.5">
            {(['edit', 'preview'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  'rounded-md px-3 py-1 text-[12px] font-semibold capitalize transition-colors',
                  mode === m ? 'bg-navy text-white' : 'text-muted hover:text-navy',
                )}
              >
                {m}
              </button>
            ))}
          </div>
          {mode === 'edit' && (
            <>
              <span className="text-[12px] font-medium text-muted" aria-live="polite">
                {conflicted ? (
                  <span className="font-semibold text-state-error-text">Changed elsewhere — reload the page</span>
                ) : save.isPending ? (
                  'Saving…'
                ) : save.isSuccess ? (
                  <span className="inline-flex items-center gap-1 text-state-success-text">
                    <Check className="size-3.5" aria-hidden /> All changes saved
                  </span>
                ) : (
                  ''
                )}
              </span>
              <button
                type="button"
                onClick={addSection}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-electric px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-[background-color,transform] hover:bg-electric/90 active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-electric"
              >
                <Plus className="size-4" aria-hidden /> Add section
              </button>
            </>
          )}
        </div>
      </div>
      <p className="m-0 mb-4 text-[13px] text-muted">
        This is the reviewed spec from your uploaded document &mdash; the source of truth the
        REGARA AI agents read. Expand any section to view it, edit it in place, reorder it, or add
        a new one; switch to <strong className="font-semibold text-navy">Preview</strong> to see
        the whole document. Changes save automatically; the original document is never altered.
      </p>
      {mode === 'edit' ? (
        <>
          {/* Find a section fast in a long spec — filters the outline by any text in the section
              (heading or body), case-insensitive, as you type. */}
          <div className="mb-3 flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (e.target.value) setJustAdded(null); // don't re-autoEdit a filtered-out new row
                }}
                placeholder="Search section titles…"
                aria-label="Search sections"
                className="w-full rounded-lg border border-line bg-white py-2 pl-9 pr-9 text-[13.5px] text-navy placeholder:text-muted focus:border-electric focus:outline-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded text-muted hover:bg-line-soft hover:text-navy"
                >
                  <X className="size-4" aria-hidden />
                </button>
              )}
            </div>
            {filtering && (
              <span className="shrink-0 text-[12px] font-medium text-muted" title="Section titles matching your search.">
                {visible.length} of {sections.length}
              </span>
            )}
          </div>
          {/* Bound the outline height + scroll internally so 100+ collapsed rows don't stretch the
              page into an endless scroll. Editing a section still happens within this area. */}
          <div className="max-h-[70vh] overflow-y-auto rounded-xl border border-line">
            {visible.length === 0 ? (
              <p className="m-0 px-4 py-8 text-center text-[13px] text-muted">
                {filtering ? <>No section title matches &ldquo;{query}&rdquo;.</> : 'No sections yet — use “Add section”.'}
              </p>
            ) : filtering ? (
              // Search results: reorder is disabled (dragging a filtered subset can't show its
              // effect on the full order), so rows render plain, without a drag handle.
              <>
                {visible.map((s) => (
                  <div key={s.id} className="border-b border-line-soft last:border-b-0">
                    <SpecSectionRow
                      section={s}
                      open={open[s.id] ?? false}
                      autoEdit={s.id === justAdded}
                      dragHandle={null}
                      onToggleOpen={onToggleOpen}
                      onCommit={onCommit}
                      onDiscard={discardSection}
                      onRequestDelete={onRequestDelete}
                    />
                  </div>
                ))}
              </>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
                  {sections.map((s) => (
                    <SortableSpecRow
                      key={s.id}
                      section={s}
                      open={open[s.id] ?? false}
                      autoEdit={s.id === justAdded}
                      onToggleOpen={onToggleOpen}
                      onCommit={onCommit}
                      onDiscard={discardSection}
                      onRequestDelete={onRequestDelete}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </>
      ) : (
        // Whole-document preview: bound the height and scroll internally so a long (86 KB+)
        // spec doesn't stretch the page into an endless scroll.
        <div className="max-h-[70vh] overflow-y-auto rounded-xl border border-line px-6 py-5">
          <SpecMarkdown markdown={previewMd} />
        </div>
      )}

      <AlertDialog open={pendingDelete !== null} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this section?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the section from your product&rsquo;s specification and can&rsquo;t be
              undone. The original uploaded document isn&rsquo;t affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (pendingDelete !== null) deleteSection(pendingDelete);
                setPendingDelete(null);
              }}
            >
              Delete section
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
